import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  ArrowRight, ArrowUpRight, Menu, X, Mail, Send,
  Users, BarChart3, Network, Target, MapPin,
  Building2, Briefcase, Heart, User,
  Shield, CheckCircle2, Calendar, Clock, Video,
  Globe, Sparkles, Share2, Play, Store, Hammer
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { LanguageProvider, useLanguage } from "./i18n";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion";
import AdminPage from "./pages/Admin";
import ReferencesPage from "./pages/References";
import LegalPage from "./pages/Legal";
import PrivacyPage from "./pages/Privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trackEvent = (name, params = {}) => {
  if (typeof window !== "undefined" && window.gtag) window.gtag("event", name, params);
};

// ===== Motion primitives =====
const EASE = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

// Chapter header with ghost number
const ChapterHeader = ({ number, kicker, title, description, light = false, align = "left" }) => (
  <motion.div
    initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer}
    className={`relative mb-16 lg:mb-20 ${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-3xl"}`}
  >
    <span className={`chapter-number chapter-number--ghost absolute -top-10 lg:-top-16 ${align === "center" ? "left-1/2 -translate-x-1/2" : "-left-2"} ${light ? "chapter-number--light" : ""}`} aria-hidden="true">
      {number}
    </span>
    <motion.p variants={fadeUp} className={`chapter-kicker relative ${light ? "chapter-kicker--light" : ""} ${align === "center" ? "justify-center" : ""}`}>
      {kicker}
    </motion.p>
    <motion.h2 variants={fadeUp} className={`relative font-display text-4xl sm:text-5xl font-bold mt-5 mb-5 ${light ? "text-white" : "text-[#142828]"}`}>
      {title}
    </motion.h2>
    {description && (
      <motion.p variants={fadeUp} className={`relative text-base md:text-lg leading-relaxed ${light ? "text-white/75" : "text-[#516969]"}`}>
        {description}
      </motion.p>
    )}
  </motion.div>
);

// ===== Cookie consent banner (RGPD) =====
const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('vf-cookie-consent') || 'null');
      const sixMonths = 182 * 24 * 3600 * 1000;
      if (stored && Date.now() - stored.date < sixMonths) {
        if (stored.choice === 'accepted' && window.loadAnalytics) window.loadAnalytics();
      } else {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const clearGACookies = () => {
    const host = window.location.hostname;
    const parent = "." + host.split(".").slice(-2).join(".");
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name === "_ga" || name.startsWith("_ga_")) {
        [host, parent, ""].forEach((d) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
        });
      }
    });
    if (window.VF_GA_ID) window[`ga-disable-${window.VF_GA_ID}`] = true;
  };

  const decide = (choice) => {
    localStorage.setItem('vf-cookie-consent', JSON.stringify({ choice, date: Date.now() }));
    setVisible(false);
    if (choice === 'accepted' && window.loadAnalytics) window.loadAnalytics();
    if (choice === 'refused') clearGACookies();
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-banner" className="fixed bottom-0 left-0 right-0 z-[110] bg-[#082A2A] border-t-2 border-[#F5B800] p-5">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
        <p className="text-sm text-white/85 flex-1">
          {t('cookies.text')}{" "}
          <a href="/confidentialite#cookies" data-testid="cookie-more-link" className="text-[#F5B800] underline underline-offset-2">{t('cookies.more')}</a>
        </p>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button onClick={() => decide('refused')} data-testid="cookie-refuse-btn"
            className="px-5 py-2.5 text-sm font-bold text-white border border-white/40 hover:border-white transition-colors uppercase tracking-wider">
            {t('cookies.refuse')}
          </button>
          <button onClick={() => decide('accepted')} data-testid="cookie-accept-btn"
            className="btn-accent px-5 py-2.5 text-sm font-bold uppercase tracking-wider">
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Newsletter Popup
const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { lang } = useLanguage();

  useEffect(() => {
    const dismissed = localStorage.getItem('vf-newsletter-dismissed');
    const subscribed = localStorage.getItem('vf-newsletter-subscribed');
    if (!dismissed && !subscribed) {
      const timer = setTimeout(() => setIsOpen(true), 15000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('vf-newsletter-dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      trackEvent("newsletter_signup");
      setIsSubscribed(true);
      localStorage.setItem('vf-newsletter-subscribed', 'true');
      toast.success(response.data.message);
      setTimeout(() => setIsOpen(false), 2000);
    } catch (error) {
      toast.error("Erreur lors de l'inscription. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = {
    fr: {
      title: "Restez informé(e)",
      subtitle: "Recevez les actualités de VF ACTIONS : solutions, IA et innovation",
      placeholder: "Votre email",
      button: "S'inscrire",
      submitting: "Inscription...",
      privacy: "Nous respectons votre vie privée. Désinscription possible à tout moment.",
      privacyLink: "Politique de confidentialité",
      success: "Merci ! Vous êtes inscrit(e)."
    },
    br: {
      title: "Chom kelaouet",
      subtitle: "Resevit keleier VF ACTIONS : diskoulmoù, IA ha neveziñ",
      placeholder: "Ho postel",
      button: "En em enskrivañ",
      submitting: "Oc'h enskrivañ...",
      privacy: "Doujañ a reomp d'ho puhez prevez. Dizenroll posupl da bep mare.",
      privacyLink: "Reolennoù kuzuliegezh",
      success: "Trugarez ! Enskrevet oc'h."
    }
  };

  const t = content[lang];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#082A2A]/60 backdrop-blur-md"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 16 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0, y: 16 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="bg-white p-8 max-w-md w-full shadow-2xl relative frame-clip"
            onClick={(e) => e.stopPropagation()}
            data-testid="newsletter-popup"
          >
            <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-[#0F4C4C] transition-colors" data-testid="newsletter-close">
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#F5B800]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-[#C49000]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#142828] mb-2">{t.title}</h3>
              <p className="text-[#516969] text-sm">{t.subtitle}</p>
            </div>

            {isSubscribed ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-[#0F4C4C] mx-auto mb-3" />
                <p className="text-[#0F4C4C] font-medium">{t.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="flex gap-2 mb-4">
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder={t.placeholder} data-testid="newsletter-email"
                    className="flex-1 bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm transition-colors"
                  />
                  <button type="submit" disabled={isSubmitting} data-testid="newsletter-submit"
                    className="btn-accent px-6 py-3 font-semibold text-sm disabled:opacity-50 whitespace-nowrap">
                    {isSubmitting ? t.submitting : t.button}
                  </button>
                </div>
                <p className="text-xs text-gray-400 text-center">
                  {t.privacy}{" "}
                  <a href="/confidentialite" target="_blank" rel="noopener noreferrer" data-testid="newsletter-privacy-link" className="underline underline-offset-2 text-[#0F4C4C]">{t.privacyLink}</a>
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Icon mapping
const iconMap = { Users, BarChart3, Network, Target };
const partnerIconList = [Building2, Briefcase, Heart, User];

// Language Switcher
const LanguageSwitcher = ({ light = false }) => {
  const { lang, toggleLanguage } = useLanguage();
  return (
    <button
      onClick={toggleLanguage}
      data-testid="language-switcher"
      className={`flex items-center gap-2 px-3 py-1.5 text-sm border transition-colors ${light ? "border-white/30 text-white hover:border-[#F5B800] hover:text-[#F5B800]" : "border-[#DAD5C8] text-[#142828] hover:border-[#0F4C4C] hover:text-[#0F4C4C]"}`}
      title={lang === 'fr' ? 'Cheñch da Vrezhoneg' : 'Passer en Français'}
    >
      <Globe size={16} />
      <span className="font-semibold">{lang === 'fr' ? 'BR' : 'FR'}</span>
    </button>
  );
};

// VF Actions Logo
const VFLogo = ({ dark = false }) => {
  const primary = dark ? "#FFFFFF" : "#0F4C4C";
  const accent = "#F5B800";
  return (
    <div className="flex items-center gap-2.5">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="36" height="36" rx="8" fill={primary} />
        <circle cx="10" cy="10" r="2" fill={accent} />
        <circle cx="26" cy="10" r="2" fill={accent} />
        <circle cx="18" cy="18" r="2.5" fill={accent} />
        <circle cx="10" cy="26" r="2" fill={accent} />
        <circle cx="26" cy="26" r="2" fill={accent} />
        <path d="M10 10 L18 18 L26 10 M10 26 L18 18 L26 26 M10 10 L10 26 M26 10 L26 26" stroke={accent} strokeWidth="1" opacity="0.6" />
      </svg>
      <span className={`font-display text-xl font-bold tracking-tight ${dark ? 'text-white' : 'text-[#142828]'}`}>
        VF ACTIONS
      </span>
    </div>
  );
};

// Header
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.platforms'), href: "#apps" },
    { name: t('nav.services'), href: "#services" },
    { name: t('nav.references'), href: "/references" },
    { name: t('nav.news'), href: "#news" },
    { name: t('nav.booking'), href: "#booking" },
    { name: t('nav.contact'), href: "#contact" }
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-[#F6F4EC]/92 backdrop-blur-lg shadow-sm border-b border-[#DAD5C8]" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a href="#" data-testid="logo" className="flex items-center">
            <VFLogo dark={!isScrolled} />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className={`link-sweep text-sm font-medium transition-colors ${isScrolled ? 'text-[#516969] hover:text-[#0F4C4C]' : 'text-white/85 hover:text-[#F5B800]'}`}>
                {link.name}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher light={!isScrolled} />
            <a href="#contact" data-testid="header-cta" className="btn-accent px-5 py-2.5 text-sm font-bold">
              {t('nav.cta')}
            </a>
          </div>

          <button data-testid="mobile-menu-btn" className={`lg:hidden p-2 ${isScrolled ? 'text-[#0F4C4C]' : 'text-white'}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="lg:hidden bg-[#F6F4EC] border-t border-[#DAD5C8] py-6">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a key={link.name} href={link.href} className="text-base text-[#516969] hover:text-[#0F4C4C]" onClick={() => setIsMenuOpen(false)}>
                  {link.name}
                </a>
              ))}
              <div className="flex items-center gap-4 mt-4">
                <LanguageSwitcher />
                <a href="#contact" className="flex-1 btn-accent px-5 py-3 text-sm font-bold text-center" onClick={() => setIsMenuOpen(false)}>
                  {t('nav.cta')}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// ===== Hero — kinetic, masked line reveal + parallax =====
const HeroSection = ({ metrics }) => {
  const { t, lang } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const metricKeys = { '1': ['carbon', 'carbonDesc'], '2': ['compliance', 'complianceDesc'], '3': ['biodiversity', 'biodiversityDesc'], '4': ['circular', 'circularDesc'] };

  return (
    <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#082A2A]">
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&q=80"
          alt="Collaboration sur le territoire"
          className="w-full h-full object-cover"
          onError={(e) => e.target.src = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80"}
        />
      </motion.div>
      <div className="hero-gradient absolute inset-0 z-[1]" />
      <div className="hero-network absolute inset-0 z-[1]" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pt-36 lg:pb-28 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: EASE }} className="engagement-badge mb-8">
              <span className="w-2 h-2 bg-[#F5B800] rounded-full animate-pulse" />
              {t('hero.subtitle')}
            </motion.div>

            {/* Masked line-by-line reveal */}
            <h1 key={lang} className="font-display text-5xl sm:text-7xl lg:text-[5.5rem] font-bold text-white mb-8 leading-[0.95] tracking-tight">
              <RevealLine delay={0.3}>{t('hero.title1')}</RevealLine>
              <RevealLine delay={0.45} className="text-[#F5B800]">{t('hero.title2')}</RevealLine>
            </h1>

            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.7, ease: EASE }} className="origin-left w-16 h-1 bg-[#F5B800] mb-8" />

            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8, ease: EASE }} className="text-lg text-white/85 max-w-2xl mb-10 leading-relaxed">
              {t('hero.description')}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.95, ease: EASE }} className="flex flex-col sm:flex-row gap-4 mb-14">
              <a href="#booking" data-testid="hero-cta-primary" className="btn-accent px-7 py-4 font-bold text-sm inline-flex items-center justify-center gap-2 uppercase tracking-wider">
                <Calendar size={18} />
                {t('hero.ctaPrimary')}
              </a>
              <a href="#apps" data-testid="hero-cta-secondary" className="btn-ghost-light px-7 py-4 font-semibold text-sm inline-flex items-center justify-center gap-2 uppercase tracking-wider">
                {t('hero.ctaSecondary')} <ArrowRight size={16} />
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.15 }} className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/15">
              {metrics.map((metric) => (
                <div key={metric.id}>
                  <p className="font-display text-3xl lg:text-4xl font-bold text-[#F5B800] stat-highlight inline-block">{metric.value}</p>
                  <p className="text-sm text-white/90 mt-2 font-medium">{t(`metrics.${metricKeys[metric.id]?.[0] || 'carbon'}`)}</p>
                  <p className="text-xs text-white/55">{t(`metrics.${metricKeys[metric.id]?.[1] || 'carbonDesc'}`)}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Network visualization */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.7, duration: 1, ease: EASE }}
            className="hidden lg:block lg:col-span-4"
          >
            <div className="relative aspect-square">
              <svg viewBox="0 0 300 300" className="w-full h-full">
                <circle cx="150" cy="150" r="35" fill="#F5B800" opacity="0.9" className="animate-node" />
                <circle cx="150" cy="150" r="48" fill="none" stroke="#F5B800" strokeWidth="1" opacity="0.4" className="connection-flow" />
                <circle cx="60" cy="80" r="18" fill="#F5B800" opacity="0.8" />
                <circle cx="240" cy="80" r="18" fill="#F5B800" opacity="0.8" />
                <circle cx="60" cy="220" r="18" fill="#F5B800" opacity="0.8" />
                <circle cx="240" cy="220" r="18" fill="#F5B800" opacity="0.8" />
                <circle cx="150" cy="50" r="14" fill="#F5B800" opacity="0.7" />
                <circle cx="150" cy="250" r="14" fill="#F5B800" opacity="0.7" />
                <line x1="150" y1="150" x2="60" y2="80" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <line x1="150" y1="150" x2="240" y2="80" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <line x1="150" y1="150" x2="60" y2="220" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <line x1="150" y1="150" x2="240" y2="220" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <line x1="150" y1="150" x2="150" y2="50" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <line x1="150" y1="150" x2="150" y2="250" stroke="#F5B800" strokeWidth="1.5" opacity="0.6" className="connection-flow" />
                <text x="150" y="156" textAnchor="middle" fill="#0F4C4C" fontFamily="Space Grotesk" fontSize="14" fontWeight="700">VF</text>
              </svg>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="w-px h-12 bg-gradient-to-b from-transparent via-[#F5B800] to-transparent" />
      </motion.div>
    </section>
  );
};

// ===== Editorial marquee =====
const EditorialMarquee = () => {
  const { t } = useLanguage();
  const words = [t('compliance.paris'), t('compliance.csrd'), t('compliance.taxonomy'), t('compliance.biodiversity'), t('hero.subtitle')];
  const strip = words.map((w, i) => (
    <span key={i} className="flex items-center gap-8 md:gap-12 mx-4 md:mx-6">
      <span className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#0F4C4C] whitespace-nowrap">{w}</span>
      <svg width="18" height="18" viewBox="0 0 18 18" className="flex-shrink-0">
        <circle cx="9" cy="9" r="4" fill="#F5B800" />
      </svg>
    </span>
  ));

  return (
    <section data-testid="marquee-section" className="bg-[#F6F4EC] border-y border-[#DAD5C8] py-8 md:py-10 overflow-hidden select-none">
      <div className="marquee-track">
        <div className="flex items-center">{strip}</div>
        <div className="flex items-center" aria-hidden="true">{words.map((w, i) => (
          <span key={`b-${i}`} className="flex items-center gap-8 md:gap-12 mx-4 md:mx-6">
            <span className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#0F4C4C] whitespace-nowrap">{w}</span>
            <svg width="18" height="18" viewBox="0 0 18 18" className="flex-shrink-0"><circle cx="9" cy="9" r="4" fill="#F5B800" /></svg>
          </span>
        ))}</div>
      </div>
    </section>
  );
};

// ===== Video showcase =====
const VideoSection = () => {
  const { t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setIsPlaying(true);
    videoRef.current?.play();
  };

  return (
    <section id="video" data-testid="video-section" className="relative py-24 lg:py-32 bg-[#0F4C4C] overflow-hidden">
      <div className="hero-network absolute inset-0 opacity-[0.08]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="lg:col-span-4">
            <motion.p variants={fadeUp} className="chapter-kicker chapter-kicker--light">{t('video.subtitle')}</motion.p>
            <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl font-bold text-white mt-5 mb-5">{t('video.title')}</motion.h2>
            <motion.p variants={fadeUp} className="text-white/75 leading-relaxed mb-8">{t('video.description')}</motion.p>
            <motion.div variants={fadeUp} className="hidden lg:flex items-center gap-3 text-[#F5B800] text-sm font-bold uppercase tracking-wider">
              <span className="w-8 h-0.5 bg-[#F5B800]" />
              vfactions.eu
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-8"
          >
            <div className="relative frame-clip overflow-hidden bg-[#082A2A] shadow-2xl">
              <video
                ref={videoRef}
                data-testid="presentation-video"
                className="w-full aspect-video object-cover"
                poster="/presentation-poster.jpg"
                preload="metadata"
                controls={isPlaying}
                playsInline
                onEnded={() => setIsPlaying(false)}
              >
                <source src="/presentation.mp4" type="video/mp4" />
              </video>

              {!isPlaying && (
                <button
                  onClick={handlePlay}
                  data-testid="video-play-btn"
                  aria-label={t('video.play')}
                  className="absolute inset-0 z-10 flex items-center justify-center group cursor-pointer"
                >
                  <span className="absolute inset-0 bg-gradient-to-t from-[#082A2A]/70 via-transparent to-transparent transition-opacity duration-300 group-hover:opacity-60" />
                  <span className="relative flex items-center justify-center">
                    <span className="absolute w-24 h-24 rounded-full bg-[#F5B800]/30 animate-ping" style={{ animationDuration: '2.2s' }} />
                    <span className="relative w-20 h-20 rounded-full bg-[#F5B800] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-xl shadow-[#F5B800]/30">
                      <Play className="w-8 h-8 text-[#082A2A] ml-1" fill="#082A2A" />
                    </span>
                  </span>
                  <span className="absolute bottom-6 left-6 text-white font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F5B800] rounded-full animate-pulse" />
                    {t('video.play')} — 0:38
                  </span>
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ===== 01 — Apps =====
const AppsSection = ({ apps }) => {
  const { t } = useLanguage();

  const getStatusClass = (status) => {
    switch (status) {
      case "Production": return "status-production";
      case "Beta": return "status-beta";
      case "Développement": return "status-development";
      default: return "status-soon";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Production": return t('apps.statusProduction');
      case "Beta": return t('apps.statusBeta');
      case "Développement": return t('apps.statusDev');
      default: return t('apps.statusSoon');
    }
  };

  return (
    <section id="apps" data-testid="apps-section" className="py-24 lg:py-36 bg-[#F6F4EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="01" kicker={t('apps.subtitle')} title={t('apps.title')} description={t('apps.description')} />

        {/* Featured platforms: Carbon Impact & VF DECIDE */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-6 lg:mb-8">
          {apps.slice(0, 2).map((app) => (
            <motion.a
              key={app.id} href={app.app_url}
              target={app.app_url.startsWith("http") ? "_blank" : "_self"} rel="noopener noreferrer"
              data-testid={`app-card-${app.id}`} variants={fadeUp}
              className="card-editorial group p-6 lg:p-8 flex flex-col"
            >
              <div className="relative overflow-hidden frame-clip frame-spotlight mb-6 h-64 lg:h-72">
                <img src={app.image_url} alt={app.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                <span className={`absolute top-4 right-4 z-[1] px-3 py-1 text-xs font-bold uppercase tracking-wider ${getStatusClass(app.status)}`}>
                  {getStatusLabel(app.status)}
                </span>
              </div>
              <div className="flex flex-col flex-grow">
                <span className="font-display text-xs font-bold text-[#C49000] uppercase tracking-[0.2em] mb-3">{app.category}</span>
                <h3 className="font-display font-bold text-[#142828] mb-3 group-hover:text-[#0F4C4C] transition-colors text-3xl">{app.name}</h3>
                <p className="text-sm text-[#516969] mb-6 leading-relaxed flex-grow">{app.description}</p>
                <span className="inline-flex items-center gap-2 text-sm text-[#0F4C4C] font-bold uppercase tracking-wider">
                  {app.app_url.startsWith("http") ? t('apps.accessPlatform') : t('apps.learnMore')}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Secondary platforms */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.slice(2).map((app) => (
            <motion.a
              key={app.id} href={app.app_url}
              target={app.app_url.startsWith("http") ? "_blank" : "_self"} rel="noopener noreferrer"
              data-testid={`app-card-${app.id}`} variants={fadeUp}
              className="card-editorial group p-5 lg:p-6 flex flex-col"
            >
              <div className="relative overflow-hidden frame-clip frame-spotlight mb-5 h-40">
                <img src={app.image_url} alt={app.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                <span className={`absolute top-3 right-3 z-[1] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${getStatusClass(app.status)}`}>
                  {getStatusLabel(app.status)}
                </span>
              </div>
              <div className="flex flex-col flex-grow">
                <span className="font-display text-[11px] font-bold text-[#C49000] uppercase tracking-[0.2em] mb-2">{app.category}</span>
                <h3 className="font-display font-bold text-[#142828] mb-2 group-hover:text-[#0F4C4C] transition-colors text-xl">{app.name}</h3>
                <p className="text-sm text-[#516969] mb-5 leading-relaxed flex-grow">{app.description}</p>
                <span className="inline-flex items-center gap-2 text-xs text-[#0F4C4C] font-bold uppercase tracking-wider">
                  {app.app_url.startsWith("http") ? t('apps.accessPlatform') : t('apps.learnMore')}
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ===== 02 — Use cases =====
const UseCasesSection = () => {
  const { t } = useLanguage();
  const cases = [
    { key: "commerce", icon: Store },
    { key: "habitant", icon: Hammer },
    { key: "commune", icon: Building2 }
  ];

  return (
    <section id="usecases" data-testid="usecases-section" className="py-24 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="02" kicker={t('useCases.subtitle')} title={t('useCases.title')} description={t('useCases.description')} />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cases.map(({ key, icon: IconComponent }) => (
            <motion.article key={key} variants={fadeUp} data-testid={`usecase-card-${key}`} className="card-editorial p-7 lg:p-8 flex flex-col">
              <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#0F4C4C] flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-[#F5B800]" />
                  </div>
                  <span className="font-display text-xs font-bold text-[#C49000] uppercase tracking-[0.18em]">{t(`useCases.cases.${key}.tag`)}</span>
                </div>
                <span data-testid={`usecase-fictional-badge-${key}`} className="text-[10px] font-bold uppercase tracking-wider text-[#516969] border border-[#DAD5C8] px-2 py-0.5 flex-shrink-0">{t('useCases.fictional')}</span>
              </div>
              <h3 className="font-display text-xl font-bold text-[#142828] mb-5">{t(`useCases.cases.${key}.title`)}</h3>
              <div className="mb-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#516969] mb-1.5">{t('useCases.before')}</p>
                <p className="text-sm text-[#516969] leading-relaxed">{t(`useCases.cases.${key}.before`)}</p>
              </div>
              <div className="mb-6 flex-grow">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0F4C4C] mb-1.5">{t('useCases.after')}</p>
                <p className="text-sm text-[#142828] leading-relaxed font-medium">{t(`useCases.cases.${key}.after`)}</p>
              </div>
              <div className="pt-5 border-t border-[#DAD5C8] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#C49000] flex-shrink-0" />
                <p className="font-display font-bold text-[#0F4C4C]">{t(`useCases.cases.${key}.result`)}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-5">
          <p className="text-xs text-[#516969] italic">{t('useCases.note')}</p>
          <a href="/references" data-testid="usecases-references-link" className="btn-accent px-7 py-4 font-bold text-sm inline-flex items-center gap-2 uppercase tracking-wider">
            {t('useCases.cta')} <ArrowRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ===== 03 — Services =====
const ServicesSection = ({ services }) => {
  const { t } = useLanguage();

  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-36 bg-[#EDEAE0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="03" kicker={t('services.subtitle')} title={t('services.title')} description={t('services.description')} />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Users;
            return (
              <motion.div key={service.id} variants={fadeUp} data-testid={`service-${service.id}`} className="card-service p-7 flex flex-col">
                <span className="manifesto-index mb-6">{String(index + 1).padStart(2, '0')}</span>
                <div className="w-12 h-12 bg-[#0F4C4C] flex items-center justify-center mb-5">
                  <IconComponent className="w-6 h-6 text-[#F5B800]" />
                </div>
                <h3 className="font-display text-lg font-bold text-[#142828] mb-3">{service.title}</h3>
                <p className="text-sm text-[#516969] leading-relaxed">{service.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ===== 03 — Partners =====
const PartnersSection = () => {
  const { t } = useLanguage();
  const partnerNames = ['collectivities', 'companies', 'associations', 'institutions'];
  const benefits = ['visibility', 'reporting', 'coconstruction', 'network'];

  return (
    <section id="partners" data-testid="partners-section" className="py-24 lg:py-36 bg-[#F6F4EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <ChapterHeader number="04" kicker={t('partners.subtitle')} title={t('partners.title')} description={t('partners.description')} />

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-2 gap-4 mb-10 -mt-4">
              {partnerNames.map((key, index) => {
                const IconComponent = partnerIconList[index];
                return (
                  <motion.div variants={fadeUp} key={key} className="flex items-start gap-3 p-5 bg-white border border-[#DAD5C8] hover:border-[#0F4C4C] transition-colors">
                    <IconComponent className="w-5 h-5 text-[#0F4C4C] mt-0.5" />
                    <div>
                      <p className="font-display font-bold text-[#142828] text-sm">{t(`partners.types.${key}.name`)}</p>
                      <p className="text-xs text-[#516969]">{t(`partners.types.${key}.type`)}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.a initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} href="#contact" className="btn-accent inline-flex items-center gap-2 px-7 py-4 font-bold text-sm uppercase tracking-wider">
              {t('partners.cta')} <ArrowRight size={16} />
            </motion.a>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} className="bg-[#0F4C4C] p-8 lg:p-10 frame-clip lg:mt-20">
            <h3 className="font-display text-2xl font-bold text-white mb-8">{t('partners.benefitsTitle')}</h3>
            <div className="space-y-6">
              {benefits.map((key) => (
                <div key={key} className="flex items-start gap-4">
                  <CheckCircle2 className="w-5 h-5 text-[#F5B800] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-display font-bold text-white text-sm mb-1">{t(`partners.benefits.${key}.title`)}</p>
                    <p className="text-sm text-white/70">{t(`partners.benefits.${key}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ===== 04 — Manifesto (About) =====
const AboutSection = () => {
  const { t } = useLanguage();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const manifesto = ['p1', 'p2', 'p3'];

  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-36 bg-[#EDEAE0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="05" kicker={t('about.subtitle')} title={t('about.title')} />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} className="relative overflow-hidden frame-clip frame-spotlight h-80 lg:h-[520px]" ref={imgRef}>
            <motion.img style={{ y: imgY }} src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" alt="L'équipe VF ACTIONS" className="w-full h-[116%] object-cover -mt-[8%]" />
            <div className="absolute bottom-5 left-5 z-[1] bg-[#F6F4EC] px-4 py-2.5 shadow-lg">
              <div className="flex items-center gap-2 text-sm text-[#142828] font-medium">
                <MapPin className="w-4 h-4 text-[#0F4C4C]" />
                <span>{t('about.location')}</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer}>
            {manifesto.map((key, index) => (
              <motion.div variants={fadeUp} key={key} className="manifesto-item py-7 px-2 flex gap-6">
                <span className="manifesto-index pt-1">{String(index + 1).padStart(2, '0')}</span>
                <p className="text-[#516969] leading-relaxed [&_strong]:text-[#142828] [&_strong]:font-display" dangerouslySetInnerHTML={{ __html: t(`about.${key}`) }} />
              </motion.div>
            ))}
            <motion.div variants={fadeUp} className="manifesto-item flex items-center gap-4 py-7 px-2">
              <Shield className="w-7 h-7 text-[#0F4C4C] flex-shrink-0" />
              <p className="text-sm text-[#516969]">{t('about.rgpd')}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ===== 05 — News =====
const NewsSection = ({ news }) => {
  const { t } = useLanguage();
  if (!news.length) return null;

  const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <section id="news" data-testid="news-section" className="py-24 lg:py-36 bg-[#F6F4EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="06" kicker={t('news.subtitle')} title={t('news.title')} description={t('news.description')} />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {news.map((item) => (
            <motion.article key={item.id} variants={fadeUp} data-testid={`news-card-${item.id}`} className="card-editorial p-7 flex flex-col">
              <div className="flex items-center justify-between mb-5">
                <span className="bg-[#F5B800] text-[#082A2A] text-xs font-bold uppercase tracking-wider px-2.5 py-1">{item.category}</span>
                <time className="text-xs text-[#516969]" dateTime={item.date}>{formatDate(item.date)}</time>
              </div>
              <h3 className="font-display text-xl font-bold text-[#142828] mb-3 leading-snug">{item.title}</h3>
              <p className="text-sm text-[#516969] leading-relaxed">{item.content}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ===== Booking =====
const BookingSection = () => {
  const { t } = useLanguage();

  const bookingOptions = [
    { key: 'discovery', icon: Video, calLink: "vincent-ferec-q0z4lk" },
    { key: 'partnership', icon: Users, calLink: "vincent-ferec-q0z4lk" },
    { key: 'demo', icon: BarChart3, calLink: "vincent-ferec-q0z4lk" }
  ];

  const openCalendly = (calLink) => {
    trackEvent("book_appointment", { link: calLink });
    window.open(`https://cal.com/${calLink}`, '_blank');
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 lg:py-36 bg-[#0F4C4C] overflow-hidden">
      <div className="hero-network absolute inset-0 opacity-[0.08]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="07" kicker={t('booking.subtitle')} title={t('booking.title')} description={t('booking.description')} light align="center" />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {bookingOptions.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <motion.div key={index} variants={fadeUp} data-testid={`booking-option-${index}`} className="bg-white/[0.07] backdrop-blur-md border border-white/15 p-7 hover:bg-white/[0.12] hover:border-[#F5B800]/40 transition-colors duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 bg-[#F5B800] flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-[#082A2A]" />
                  </div>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{t(`booking.options.${option.key}.duration`)}</span>
                  </div>
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-3">{t(`booking.options.${option.key}.title`)}</h3>
                <p className="text-sm text-white/70 mb-6 leading-relaxed">{t(`booking.options.${option.key}.desc`)}</p>
                <button onClick={() => openCalendly(option.calLink)} data-testid={`booking-btn-${index}`} className="w-full bg-white text-[#0F4C4C] py-3 font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#F5B800] hover:text-[#082A2A] transition-colors uppercase tracking-wider">
                  <Calendar className="w-4 h-4" />
                  {t('booking.bookSlot')}
                </button>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center text-white/55 text-sm mt-10">
          {t('booking.videoNote')}
        </motion.p>
      </div>
    </section>
  );
};

// ===== 08 — FAQ =====
const FAQSection = () => {
  const { t } = useLanguage();
  const items = ["q1", "q2", "q3", "q4", "q5"];

  return (
    <section id="faq" data-testid="faq-section" className="py-24 lg:py-36 bg-[#EDEAE0]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <ChapterHeader number="08" kicker={t('faq.subtitle')} title={t('faq.title')} description={t('faq.description')} />

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp}>
          <Accordion type="single" collapsible className="space-y-3">
            {items.map((key) => (
              <AccordionItem key={key} value={key} data-testid={`faq-item-${key}`} className="bg-white border border-[#DAD5C8] px-6 data-[state=open]:border-[#0F4C4C]">
                <AccordionTrigger className="font-display font-bold text-[#142828] text-left hover:no-underline hover:text-[#0F4C4C] py-5">
                  {t(`faq.items.${key}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#516969] leading-relaxed pb-5">
                  {t(`faq.items.${key}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-10">
          <a href="#contact" data-testid="faq-contact-link" className="link-sweep inline-flex items-center gap-2 text-[#0F4C4C] font-bold text-sm uppercase tracking-wider">
            {t('faq.cta')} <ArrowRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ===== 09 — Contact =====
const ContactSection = () => {
  const { t } = useLanguage();
  const emptyContact = { name: "", email: "", organization: "", organization_type: "Artisan", message: "" };
  const [formData, setFormData] = useState(emptyContact);
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, consent });
      trackEvent("generate_lead", { method: "contact_form" });
      toast.success(t('contact.form.success'));
      setFormData(emptyContact);
      setConsent(false);
    } catch (error) {
      toast.error(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-[#F6F4EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <ChapterHeader number="09" kicker={t('contact.subtitle')} title={t('contact.title')} description={t('contact.description')} />

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="space-y-6 -mt-4">
              <motion.div variants={fadeUp} className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0F4C4C] flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-[#F5B800]" />
                </div>
                <div>
                  <p className="text-sm text-[#516969] mb-1">{t('contact.email')}</p>
                  <a href="mailto:vferec29@gmail.com" className="link-sweep text-[#142828] font-medium hover:text-[#0F4C4C] transition-colors">vferec29@gmail.com</a>
                </div>
              </motion.div>
              <motion.div variants={fadeUp} className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0F4C4C] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-[#F5B800]" />
                </div>
                <div>
                  <p className="text-sm text-[#516969] mb-1">{t('contact.headquarters')}</p>
                  <p className="text-[#142828] font-medium">{t('about.location')}</p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white border border-[#DAD5C8] p-8 lg:p-10 frame-clip">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">{t('contact.form.name')} *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name-input" className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm" placeholder={t('contact.form.namePlaceholder')} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">{t('contact.form.email')} *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="contact-email-input" className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm" placeholder={t('contact.form.emailPlaceholder')} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">{t('contact.form.organization')} *</label>
                  <input type="text" name="organization" value={formData.organization} onChange={handleChange} required data-testid="contact-organization-input" className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm" placeholder={t('contact.form.organizationPlaceholder')} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">{t('contact.form.orgType')} *</label>
                  <select name="organization_type" value={formData.organization_type} onChange={handleChange} required data-testid="contact-type-select" className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm">
                    <option value="Artisan">{t('contact.orgTypes.artisan')}</option>
                    <option value="Commerce">{t('contact.orgTypes.commerce')}</option>
                    <option value="Entreprise">{t('contact.orgTypes.company')}</option>
                    <option value="Association">{t('contact.orgTypes.association')}</option>
                    <option value="Collectivité">{t('contact.orgTypes.collectivity')}</option>
                    <option value="Autre">{t('contact.orgTypes.other')}</option>
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">{t('contact.form.message')} *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} data-testid="contact-message-input" className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-[#142828] text-sm resize-none" placeholder={t('contact.form.messagePlaceholder')} />
              </div>
              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required data-testid="contact-consent-checkbox"
                  className="mt-0.5 w-4 h-4 accent-[#0F4C4C] flex-shrink-0" />
                <span className="text-xs text-[#516969] leading-relaxed">
                  {t('contact.form.consent')}{" "}
                  <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#0F4C4C] font-medium underline underline-offset-2">{t('contact.form.consentLink')}</a>. *
                </span>
              </label>
              <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="w-full btn-accent py-4 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 uppercase tracking-wider">
                {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')} <Send size={16} />
              </button>
              <p data-testid="contact-reassurance" className="text-xs text-[#516969] text-center mt-4 flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#0F4C4C]" /> {t('contact.form.reassurance')}
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="footer-gradient text-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <VFLogo dark={true} />
            <p className="text-white/70 text-sm max-w-sm leading-relaxed mt-5">{t('footer.description')}</p>
            <div className="flex items-center gap-2 mt-6 text-xs text-white/60">
              <span className="w-2 h-2 bg-[#F5B800] rounded-full animate-pulse" />
              <span>{t('about.location')}</span>
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold mb-5 text-xs uppercase tracking-[0.2em] text-[#F5B800]">{t('footer.navigation')}</h4>
            <ul className="space-y-3">
              <li><a href="#apps" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">{t('nav.platforms')}</a></li>
              <li><a href="#services" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">{t('nav.services')}</a></li>
              <li><a href="#partners" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">{t('nav.partners')}</a></li>
              <li><a href="/references" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">{t('nav.references')}</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-5 text-xs uppercase tracking-[0.2em] text-[#F5B800]">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li><a href="#contact" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">{t('footer.form')}</a></li>
              <li><a href="mailto:vferec29@gmail.com" className="link-sweep text-sm text-white/70 hover:text-[#F5B800] transition-colors">vferec29@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/50">© {currentYear} VF ACTIONS - {t('footer.rights')}</p>
          <div className="flex items-center gap-6">
            <a href="/mentions-legales" className="text-xs text-white/50 hover:text-[#F5B800] transition-colors">{t('footer.legal')}</a>
            <a href="/confidentialite" className="text-xs text-white/50 hover:text-[#F5B800] transition-colors">{t('footer.privacy')}</a>
            <a href="/admin" data-testid="footer-admin-link" className="text-xs text-white/50 hover:text-[#F5B800] transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Landing Page
function LandingPage() {
  const [apps, setApps] = useState([]);
  const [services, setServices] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, anchors: { offset: -70 } });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appsRes, servicesRes, metricsRes, newsRes] = await Promise.all([
          axios.get(`${API}/apps`),
          axios.get(`${API}/services`),
          axios.get(`${API}/metrics`),
          axios.get(`${API}/news`)
        ]);
        setApps(appsRes.data);
        setServices(servicesRes.data);
        setMetrics(metricsRes.data);
        setNews(newsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return <AppContent apps={apps} services={services} metrics={metrics} news={news} loading={loading} />;
}

// Main App
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/references" element={<ReferencesPage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/confidentialite" element={<PrivacyPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
    </LanguageProvider>
  );
}

const AppContent = ({ apps, services, metrics, news, loading }) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F4EC] flex items-center justify-center">
        <div className="text-[#516969] text-sm">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="app-container grain">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <HeroSection metrics={metrics} />
        <EditorialMarquee />
        <VideoSection />
        <AppsSection apps={apps} />
        <UseCasesSection />
        <ServicesSection services={services} />
        <PartnersSection />
        <AboutSection />
        <NewsSection news={news} />
        <BookingSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <NewsletterPopup />
    </div>
  );
};

export default App;
